import React, { useEffect, useRef } from 'react'
import { Flex, Icon, Heading, Text, Button, Theme, useIsTheme } from '@afterpaytouch/core'
import clsx from 'clsx'
import { CheckoutPage } from '../../model'
import { useTranslation } from '../../utils/convergence'
import styles from './styles.module.css'
import { useCheckoutSizes } from '../../utils/responsive'
import { TrackPageViewed } from '../../integrations/monitoring'
import { useCloseCheckout } from '../../utils/close'
import { useIsAppleRetailAppleStoreAppOrder, useSendEndTransactionMessage } from '../../state'

export const PurchaseSuccessPage: CheckoutPage = () => {
  const { t } = useTranslation()
  const { headingSize, textSize } = useCheckoutSizes()
  const isKelpie = useIsTheme(Theme.Kelpie)
  const isKelpieMint = useIsTheme(Theme.KelpieMint)
  const isKelpieFamily = isKelpie || isKelpieMint

  // ProcessingModal freezes the send-end-transaction decision before navigating here so
  // post-mount PostCheckout eligibility flips (from asset CTA clicks writing a tag) can't
  // flip us from suppress → send. Fall back to true only for a direct visit where the
  // ProcessingModal handoff was skipped.
  const sendEndTransactionMessage = useSendEndTransactionMessage() ?? true

  // Capture close(), the send-end-transaction flag, and the order-source flag on first
  // render: close() clears the checkout session, so these need to survive that so the
  // manual button keeps working (and its label doesn't change) after the deeplink
  // prompt is dismissed.
  const closeRef = useRef(useCloseCheckout())
  const sendEndTransactionMessageRef = useRef(sendEndTransactionMessage)
  const isAppleStoreAppOrderRef = useRef(useIsAppleRetailAppleStoreAppOrder())

  // Auto-redirect back to the merchant/app on mount — the same close()/redirect the
  // processing modal runs. If the customer dismisses the OS deeplink prompt they are
  // left on this page and can re-trigger it with the button.
  useEffect(() => {
    void closeRef.current(sendEndTransactionMessageRef.current)
  }, [])

  const handleReturn = (): void => {
    void closeRef.current(sendEndTransactionMessageRef.current)
  }

  const subtitle = isAppleStoreAppOrderRef.current ? t('purchaseSuccess:subtitleAppleStoreApp') : t('purchaseSuccess:subtitle')
  const buttonLabel = isAppleStoreAppOrderRef.current ? t('purchaseSuccess:openAppleStoreAppButton') : t('purchaseSuccess:returnToMerchant')

  return (
    <>
      <Flex direction='column'>
        <Flex alignItems='center' justifyContent='center' className={clsx(styles.iconWrapper, isKelpieFamily && styles.iconWrapperKelpie)}>
          <Icon name='Check' size='Medium' color={isKelpieFamily ? 'Black' : 'White'} />
        </Flex>
        <Heading size={headingSize} className={styles.heading}>
          {t('purchaseSuccess:heading')}
        </Heading>
        <div className={styles.subtitle}>
          <Text size={textSize} color='Gray50'>
            {subtitle}
          </Text>
        </div>
        <Button kind='Secondary' padding='Fluid' onClick={handleReturn}>
          {buttonLabel}
        </Button>
      </Flex>
      <TrackPageViewed />
    </>
  )
}
